import React, { useRef } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { type SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { isAppLoading } from '@/core/features/loader';
import TextField from '@/components/TextField';
import SelectField from '@/components/SelectField';
import Form, { type FormElement } from '@/components/Form';
import { roleOptions, inviteMember } from '@/slices/members';
import { type RootState } from '@/store';
import { normalizeSimpleFormOptions, useYupValidationResolver } from '@/utils';
import { ModalLayout } from '../../layouts';
import { type ModalContentProps } from '../../../state';
import styles from './styles';

const validationSchema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  role_id: yup.string().required('Role is required'),
});

const defaultValues: FormValues = {
  email: '',
  role_id: '',
};

type FormValues = {
  email: string;
  role_id?: string;
};

type Props = ModalContentProps & ConnectedProps<typeof connector>;

const InviteMemberModal = ({
  closeModal,
  modalData: { propertyId },
  roleOptions,
  isAppLoading,
  inviteMember,
}: Props) => {
  const formRef = useRef<FormElement>(null);
  const resolver = useYupValidationResolver(validationSchema);

  const handleAccept = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const onSubmit: SubmitHandler<FormValues> = ({ email, role_id }) => {
    inviteMember({
      propertyId,
      member: {
        email,
        role_id: Number(role_id),
      },
    });
  };

  return (
    <ModalLayout
      closeModal={closeModal}
      title="Invite Member"
      onAccept={handleAccept}
      acceptText={isAppLoading ? 'Inviting...' : 'Invite'}
      acceptDisabled={isAppLoading}
      onCancel={closeModal}
      cancelText="Cancel"
    >
      <Form
        ref={formRef}
        sx={styles.form}
        resolver={resolver}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
      >
        <TextField name="email" label="Email" type="email" />

        <SelectField
          name="role_id"
          label="Role"
          options={normalizeSimpleFormOptions(roleOptions)}
        />
      </Form>
    </ModalLayout>
  );
};

const connector = connect(
  (state: RootState) => ({
    roleOptions: roleOptions(state),
    isAppLoading: isAppLoading(state),
  }),
  { inviteMember },
);

export default connector(InviteMemberModal);
